import React, { useEffect } from "react";
import { useTranslate, useCreate } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Row,
  Col,
  Button,
  Select,
  Switch,
  Tabs,
  Badge,
} from "antd";
import { SaveOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";

type PricingPlanCreateModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const PricingPlanCreateModal: React.FC<PricingPlanCreateModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const [form] = Form.useForm();

  const { mutate: createPlan, isLoading: isCreating } = useCreate();

  const { selectProps: productSelectProps } = useSelect({
    resource: "products",
    optionLabel: "name",
    optionValue: "id",
  });

  const watchedProducts = Form.useWatch("products", form);
  const productCount =
    watchedProducts?.filter((p: any) => p?.productId).length ?? 0;

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const validateDuplicateProduct =
    (fieldIndex: number) => (_: any, value: any) => {
      if (!value) return Promise.resolve();
      const products = form.getFieldValue("products") || [];
      const duplicateCount = products.filter(
        (p: any, i: number) => p?.productId === value && i !== fieldIndex,
      ).length;
      if (duplicateCount > 0) {
        return Promise.reject(
          new Error(_t("pricing-plans.errors.duplicateProduct")),
        );
      }
      return Promise.resolve();
    };

  const handleFinish = (values: any) => {
    createPlan(
      { resource: "pricing-plans", values },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  return (
    <Modal
      title={_t("pricing-plans.titles.create")}
      open={open}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isCreating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="!mt-2"
      >
        <Tabs
          defaultActiveKey="info"
          items={[
            {
              key: "info",
              label: _t("pricing-plans.fields.planInfo"),
              children: (
                <div className="pt-2">
                  <Row gutter={[24, 0]}>
                    <Col xs={24} sm={16}>
                      <Form.Item
                        label={_t("pricing-plans.fields.name")}
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: _t("fields.errors.required"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label={_t("pricing-plans.fields.isActive")}
                        name="isActive"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={24}>
                      <Form.Item
                        label={_t("pricing-plans.fields.description")}
                        name="description"
                      >
                        <Input.TextArea rows={4} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: "products",
              label: (
                <Space size={6}>
                  {_t("pricing-plans.fields.productsSection")}
                  {productCount > 0 && (
                    <Badge count={productCount} color="orange" />
                  )}
                </Space>
              ),
              children: (
                <div className="pt-2">
                  <Form.List name="products">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.length > 0 && (
                          <div className="flex gap-2 mb-1 px-1">
                            <span className="flex-1 text-xs text-gray-400">
                              {_t("pricing-plans.fields.product")}
                            </span>
                            <span className="w-[160px] text-xs text-gray-400">
                              {_t("pricing-plans.fields.customPrice")}
                            </span>
                            <span className="w-8" />
                          </div>
                        )}
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key} className="flex gap-2 items-start">
                            <Form.Item
                              {...restField}
                              name={[name, "productId"]}
                              className="flex-1"
                              rules={[
                                {
                                  required: true,
                                  message: _t("fields.errors.required"),
                                },
                                { validator: validateDuplicateProduct(name) },
                              ]}
                            >
                              <Select {...productSelectProps} />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "customPrice"]}
                              className="w-[160px]"
                            >
                              <InputNumber
                                min={0}
                                className="w-full"
                                addonAfter="Cr."
                              />
                            </Form.Item>
                            <Form.Item>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Form.Item>
                          </div>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                          >
                            {_t("pricing-plans.actions.addProduct")}
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </div>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};
